import React, { useCallback, useState } from 'react';
import { Box, CircularProgress, Grid, IconButton, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Namespace } from '../../../../locales/translations';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import CampaignAddressesMethods from '../../../../actions/campaignAddresses';
import ActionButton from '../../../_include/Buttons/ActionButton';
import { showError } from '../../../../store/reducers/snacks';
import { selectAllCampaignAddresses } from '../../../../store/reducers/awareness_campaigns/addresses';
import { Refresh } from '@mui/icons-material';
import theme from '../../../../constants/theme';
import NavigationDialog from '../../NavigationDialog';
import { AwarenessCampaignStatus } from '../../../../models/AwarenessCampaign';
import NavigationIcon from '@mui/icons-material/NavigationRounded';
import RouteIcon from '@mui/icons-material/RouteRounded';
import { usePartnerID } from '../../../../hooks/partners';

const CampaignAddressesListHeader: React.FC = () => {
    const dispatch = useAppDispatch();

    const { t } = useTranslation([Namespace.ADDRESSES, Namespace.SNACKS, Namespace.ACTIONS]);

    const userLocation = useAppSelector(state => state.user.location);
    const partnerID = usePartnerID();
    const loading = useAppSelector((state) => state.awareness_campaign.addresses.loading);
    const addresses = useAppSelector(state => selectAllCampaignAddresses(state));
    const error = useAppSelector((state) => state.awareness_campaign.selected.error);
    const missionStatus = useAppSelector((state) => state.awareness_campaign.selected.selectedAwarenessCampaign?.status);
    const isSorted = useAppSelector((state) => state.awareness_campaign.addresses.sorted);

    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [isNavigationModalOpen, setIsNavigationModalOpen] = useState(false); // Track navigation dialog state


    // Handle sorting addresses
    const handleSortAddresses = useCallback(async () => {
        const startingPoint = {
            latitude: userLocation?.lat || 0,
            longitude: userLocation?.lng || 0,
        };

        await dispatch(
            CampaignAddressesMethods.optimizeAddressRoute(partnerID!, startingPoint, addresses)
        );
    }, [addresses, partnerID, dispatch, userLocation?.lat, userLocation?.lng]);

    // Handle start navigation button click
    const handleStartNavigation = useCallback(() => {
        if (addresses.length > 0) {
            setIsNavigationModalOpen(true); // Open the navigation dialog
        } else {
            dispatch(showError(t('no_addresses_to_navigate', { ns: Namespace.SNACKS })));
        }
    }, [addresses, dispatch, t]);


    if (error) {
        return (
            <Typography variant="body1" color="error">
                {t('error_loading_awareness_campaign', { ns: Namespace.SNACKS })}
            </Typography>
        );
    }

    return (
        <Box px={2} pt={1} pb={3}>
            <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
            >
                {/* Title Grid Item */}
                <Grid
                    item
                    xs={12}
                    sm={5}
                    display="flex"
                    flexDirection='column'
                    justifyContent={isSmallScreen ? 'center' : 'flex-start'}
                    gap={1}
                >
                    <Typography
                        variant="h5"
                        color="text.secondary"
                        margin={0}
                        align={isSmallScreen ? 'center' : 'left'}
                    >
                        {t('addresses_sensitized', {
                            ns: Namespace.ADDRESSES,
                            count: addresses.filter((a) => a.visited).length || 0,
                            total: addresses.length || 0,
                        })}
                    </Typography>
                    
                    {isSorted &&
                        <Typography pt={1} variant="body2" color="grey" align={isSmallScreen ? 'center' : 'left'}>
                            {t('addresses_sorted_for_you', { ns: Namespace.ADDRESSES })}
                        </Typography>
                    }
                </Grid>
                {isSorted && (
                    <Grid
                        item
                        xs={12}
                        sm={2}
                        display="flex"
                        justifyContent={isSmallScreen ? 'center' : 'flex-end'}
                        alignItems="center"
                        gap={1}
                    >
                        <IconButton
                            aria-label="refresh"
                            size="medium"
                            color="inherit"
                            onClick={handleSortAddresses}
                            disabled={loading}
                        >
                            {loading ? (
                                <CircularProgress size={20} />
                            ) : (
                                <Refresh fontSize="inherit" />
                            )}
                        </IconButton>
                    </Grid>
                )}

                {/* Actions Grid Item */}
                {missionStatus !== AwarenessCampaignStatus.COMPLETED &&
                    <Grid
                        item
                        xs={12}
                        sm={5}
                        display="flex"
                        justifyContent={isSmallScreen ? 'center' : 'flex-end'}
                        alignItems="center"
                        gap={1}
                    >
                        <ActionButton
                            fullWidth={isSmallScreen}
                            size='normal'
                            loading={loading}
                            onClick={isSorted ? handleStartNavigation : handleSortAddresses}
                            startIcon={isSorted ? <NavigationIcon /> : <RouteIcon />}
                        >
                            {isSorted
                                ? t('start_navigation', { ns: Namespace.ACTIONS })
                                : t('generate_an_itinerary', { ns: Namespace.ACTIONS })}
                        </ActionButton>
                    </Grid>
                }
            </Grid>


            {/* Navigation Dialog */}
            {isSorted && addresses.length > 0 && (
                <NavigationDialog
                    variant="route"
                    open={isNavigationModalOpen}
                    onClose={() => setIsNavigationModalOpen(false)}
                    addresses={addresses}
                />
            )}
        </Box>
    );
};

export default CampaignAddressesListHeader;